import { createContext, JSX, useContext } from 'react';
import { FirebaseApp } from "firebase/app"
import { Auth, User } from "firebase/auth"
import { DataStates } from './providers/FirebaseProvider';

export const AppContext: React.Context<FirebaseApp | null> = createContext(null as FirebaseApp | null);
export const AuthContext: React.Context<{
    auth: Auth,
    user: User | null,
} | null> = createContext(null as {
    auth: Auth,
    user: User | null,
} | null);
export const DataContext: React.Context<DataStates<unknown> | null> = createContext(null as DataStates<unknown> | null);
export const NavbarControllerContext: React.Context<ReturnType<typeof useNavbarController> | null> = createContext(null as ReturnType<typeof useNavbarController> | null);
export const SidebarControllerContext: React.Context<ReturnType<typeof useSidebarController> | null> = createContext(null as ReturnType<typeof useSidebarController> | null);

export const DialogControllerContext: React.Context<ReturnType<typeof useDialogController> | null> = createContext(null as ReturnType<typeof useDialogController> | null);
export const CurrentDialogContext: React.Context<ReturnType<typeof useCurrentDialog> | null> = createContext(null as ReturnType<typeof useCurrentDialog> | null);

export function useDatabase<T>(): DataStates<T> {
    const context = useContext(DataContext);
    if (context == null) {
        throw new Error('useDatabase must be used within a DataProvider context');
    }
    return context as DataStates<T>;
}

export function useAuth(): {
    auth: Auth,
    user: User | null,
} {
    const context = useContext(AuthContext);
    if (context == null) {
        throw new Error('useAuth must be used within a GlobalProvider context');
    }
    return context;
}

export function useApp(): {
    app: FirebaseApp
} {
    const context = useContext(AppContext);
    if (context == null) {
        throw new Error('useApp must be used within a GlobalProvider context');
    }
    return {
        app: context
    };
}

export function useNavbarController(): {
    queueNavbar: (depth: number, func: () => JSX.Element) => () => void;
    removeNavbar: (id: string) => void;
} {
    const context = useContext(NavbarControllerContext);
    if (context == null) {
        throw new Error('useNavbarController must be used within a GlobalProvider context');
    }
    return context;
}

export function useSidebarController(): {
    queueSidebar: (depth: number, func: () => JSX.Element) => () => void;
    removeSidebar: (id: string) => void;
    getSidebarState: () => boolean;
    setSidebarState: (state: boolean) => void;
} {
    const context = useContext(SidebarControllerContext);
    if (context == null) {
        throw new Error('useSidebarController must be used within a GlobalProvider context');
    }
    return context;
}

export function useDialogController(): {
    showDialog: (content: () => JSX.Element, buttons: [string, (close: () => void) => void][], enableclose: boolean, title: string) => string,
    hideDialog: (id: string) => void,
    showDialogAsync: (content: () => JSX.Element, buttons: string[], enableclose: boolean, title: string) => Promise<string>,
} {
    const context = useContext(DialogControllerContext);
    if (context == null) {
        throw new Error('useDialogController must be used within a GlobalProvider context');
    }
    return context;
}

export function useCurrentDialog(): {
    hideDialog: () => void,
} {
    const context = useContext(CurrentDialogContext);
    if (context == null) {
        throw new Error('useCurrentDialog must be used within a dialog');
    }
    return context;
}
